import { getGeneralInfo, getUserDetails } from "Config/API/get";

export const isEmptyStripeConnectedId = (id) => {
  if (id == null) return true;
  const normalized = String(id).trim();
  return normalized === "" || normalized === "null" || normalized === "undefined";
};

const getSuperadminOrgId = (loggedInUser, generalInfo) =>
  loggedInUser?.org_id ??
  generalInfo?.org_id ??
  generalInfo?.org_info?.org_id;

export const isSuperadminOrgAdmin = (loggedInUser, generalInfo) => {
  const role = loggedInUser?.role?.toString()?.toLowerCase();
  if (role !== "admin") return false;

  return getSuperadminOrgId(loggedInUser, generalInfo)?.toString() === "1";
};

export const isSuperadminOrgEmployee = (loggedInUser, generalInfo) => {
  const role = loggedInUser?.role?.toString()?.toLowerCase();
  if (role !== "employee") return false;

  return getSuperadminOrgId(loggedInUser, generalInfo)?.toString() === "1";
};

export const getStripeConnectedIdFromUserData = (userData) => {
  if (!userData) return null;

  const id = userData?.stripe_connected_id ?? null;
  if (isEmptyStripeConnectedId(id)) return null;
  return id;
};

export const getStripeConnectedIdFromAdminProfile = (adminProfileResponse) => {
  if (!adminProfileResponse) return null;

  const id =
    adminProfileResponse?.org_info?.stripe_connected_id ??
    adminProfileResponse?.stripe_connected_id ??
    null;

  if (isEmptyStripeConnectedId(id)) return null;
  return id;
};

export const getUserStripeConnectedIdFromAdminProfile = (adminProfileResponse) => {
  if (!adminProfileResponse) return null;

  const id =
    adminProfileResponse?.org_info?.user_stripe_connected_id ??
    adminProfileResponse?.user_stripe_connected_id ??
    null;

  if (isEmptyStripeConnectedId(id)) return null;
  return id;
};

const resolveOrgStripeConnectedId = (loggedInUser, generalInfo) => {
  const orgInfo = generalInfo?.org_info ?? generalInfo;
  return (
    getStripeConnectedIdFromAdminProfile({
      org_info: orgInfo,
      stripe_connected_id: orgInfo?.stripe_connected_id,
    }) ??
    getStripeConnectedIdFromAdminProfile({
      stripe_connected_id: loggedInUser?.stripe_connected_id,
    })
  );
};

const resolveSuperadminOrgEmployeeStripeConnectedId = (
  loggedInUser,
  generalInfo
) =>
  getUserStripeConnectedIdFromAdminProfile({
    org_info: generalInfo,
    user_stripe_connected_id: generalInfo?.user_stripe_connected_id,
  });

export const resolveStripeConnectedAccountId = (loggedInUser, generalInfo) => {
  const role = loggedInUser?.role?.toString()?.toLowerCase();

  if (isSuperadminOrgAdmin(loggedInUser, generalInfo)) {
    return getStripeConnectedIdFromUserData(loggedInUser);
  }

  if (isSuperadminOrgEmployee(loggedInUser, generalInfo)) {
    return resolveSuperadminOrgEmployeeStripeConnectedId(
      loggedInUser,
      generalInfo
    );
  }

  if (role === "employee") {
    return resolveOrgStripeConnectedId(loggedInUser, generalInfo);
  }

  return getStripeConnectedIdFromUserData(loggedInUser);
};

export const fetchStripeConnectedAccountId = async (
  token,
  loggedInUser,
  generalInfo
) => {
  if (isSuperadminOrgAdmin(loggedInUser, generalInfo)) {
    const cached = getStripeConnectedIdFromUserData(loggedInUser);
    if (cached) return cached;

    const userRes = await getUserDetails(token);
    if (userRes?.data?.status) {
      return getStripeConnectedIdFromUserData(userRes.data.data);
    }
    return null;
  }

  if (isSuperadminOrgEmployee(loggedInUser, generalInfo)) {
    const cached = resolveSuperadminOrgEmployeeStripeConnectedId(
      loggedInUser,
      generalInfo
    );
    if (cached) return cached;

    const adminRes = await getGeneralInfo(token);
    if (adminRes?.status) {
      return getUserStripeConnectedIdFromAdminProfile(adminRes);
    }
    return null;
  }

  const role = loggedInUser?.role?.toString()?.toLowerCase();

  if (role === "employee") {
    const cached = resolveStripeConnectedAccountId(loggedInUser, generalInfo);
    if (cached) return cached;

    const adminRes = await getGeneralInfo(token);
    if (adminRes?.status) {
      return getStripeConnectedIdFromAdminProfile(adminRes);
    }
    return null;
  }

  return resolveStripeConnectedAccountId(loggedInUser, generalInfo);
};

export const enrichUserDataWithStripeConnectedId = async (
  token,
  userData,
  generalInfo
) => {
  if (!userData || !isSuperadminOrgEmployee(userData, generalInfo)) {
    return userData;
  }

  if (!isEmptyStripeConnectedId(userData.stripe_connected_id)) {
    return userData;
  }

  let stripeId = resolveSuperadminOrgEmployeeStripeConnectedId(
    userData,
    generalInfo
  );

  if (!stripeId && token) {
    const adminRes = await getGeneralInfo(token);
    if (adminRes?.status) {
      stripeId = getUserStripeConnectedIdFromAdminProfile(adminRes);
    }
  }

  if (!stripeId) return userData;

  return { ...userData, stripe_connected_id: stripeId };
};
